// src/Components/Sessions/NewPatientSession.jsx
import React, { useRef, useState, useEffect } from "react";
import { Row, Col, Container, Button } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import {
  getAppointmentById,
  getServiceById,
  fetchSessionByAppointmentId,
} from "../../api";
import { FollowUpSession } from "./FollowUpSession";
import { UpdateSession } from "./EditSession";
import { getBookingsReturnPath } from "../../utils/bookingsReturn";

export default function NewPatientSession({ onMobileNavigate }) {
  const followUpRef = useRef();
  const navigate = useNavigate();

  // Only expect an appointment-based route
  const { appointmentId } = useParams();

  const [appointment, setAppointment] = useState();
  const [service, setService] = useState();
  const [existingSessionId, setExistingSessionId] = useState(null);
  const [existingSession, setExistingSession] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  // Left sidebar (mobile)
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);

  // Always resolve a safe patientId (unregistered => "1")
  const effectivePatientId = appointment?.patientId || "1";

  const formattedDateTime =
    appointment?.start && !isNaN(new Date(appointment.start))
      ? new Intl.DateTimeFormat("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
          day: "2-digit",
          month: "short",
          year: "numeric",
        }).format(new Date(appointment.start))
      : "";

  const NotesHeader = () => {
    return (
      <div className="appt-sticky-header">
        <Row className="align-items-center">
          <Col md={12}>
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
              <p className="text-primary fw-bold mb-0">
                {formattedDateTime} &nbsp;-&nbsp;{" "}
                {service?.servicesName || "Loading service name..."}
              </p>
            </div>
          </Col>
        </Row>

      </div>
    );
  };

  const renderAppointmentNotes = () => {
    if (!appointment) return <p>Loading appointment details...</p>;

    // If a session already exists for this appointment -> edit; else create new
    if (existingSessionId && existingSession) {
      return (
        <>
          <NotesHeader />
          <UpdateSession
            key={`edit-${existingSessionId}`}
            sessionId={existingSession._id}
            appointmentId={existingSession.sessionsAppointmentId}
            practiceId={existingSession.sessionsPracticeId}
            patientId={effectivePatientId}
            sections={existingSession.sections}
            notepadContentR={existingSession.notepadContent || ""}
            notepadContentType={existingSession.notepadContentType || ""}
            handwritingImageR={existingSession.handwritingImage || null}
            mediaFilesR={existingSession.mediaFiles || []}
            appointmentStatus={appointment.status}
            serviceName={service?.servicesName || ""}
            appointmentStart={appointment.start}
            templateId={existingSession.sessionsTemplateId}
            resourceId={appointment.resourceId}
            capturedValuesR={existingSession.capturedValues || {}}
            sessionsTemplateVersionR={existingSession.sessionsTemplateVersion}
            updatedAtR={existingSession.updatedAt || null}
            // After any save, go to /bookings (do not change appointment status here)
            onSaved={() => navigate(getBookingsReturnPath())}
          />
        </>
      );
    }

    return (
      <>
        <NotesHeader />
        <FollowUpSession
          key={`create-${appointmentId}`}
          ref={followUpRef}
          appointmentId={appointmentId}
          patientId={effectivePatientId}
          serviceName={service?.servicesName || ""}
          resourceId={appointment.resourceId}
          appointmentStart={appointment.start}
          appointmentStatus={appointment.status}
          practiceId={appointment.practiceId}
          // After submit/save, go to /bookings (do not change appointment status here)
          onUpdate={() => navigate(getBookingsReturnPath())}
          onSaved={() => navigate(getBookingsReturnPath())}
        />
      </>
    );
  };

  const doAndCollapse = (fn, payload) => {
    try {
      fn?.();
    } finally {
      setIsSidebarCollapsed(true);
      onMobileNavigate?.(payload);
    }
  };

  // --- Effects ---
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Load appointment + service
  useEffect(() => {
    async function loadAppointment() {
      try {
        if (!appointmentId) return;

        const appt = await getAppointmentById(appointmentId);
        setAppointment(appt);

        if (appt?.serviceId) {
          const svc = await getServiceById(appt.serviceId);
          setService(svc);
        }
      } catch (error) {
        console.error("Error fetching appointment/service:", error);
      } finally {
        setIsLoading(false);
      }
    }
    loadAppointment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentId]);

  // Load existing session (by appointment)
  useEffect(() => {
    async function loadSession() {
      try {
        if (!appointmentId) return;
        const s = await fetchSessionByAppointmentId(appointmentId);
        if (s && s._id) {
          setExistingSessionId(s._id);
          setExistingSession(s);
        }
      } catch (error) {
        console.error("Error fetching session:", error);
      }
    }
    loadSession();
  }, [appointmentId]);

  if (isLoading) return <p>Loading session data...</p>;

  return (
    <div className="ms-1">
      {/* Mobile-only Collapsible Arrow Button */}
      {isMobile && (
        <div
          className="position-fixed d-flex align-items-center justify-content-center bg-primary text-white shadow-lg"
          style={{
            left: isSidebarCollapsed ? "10px" : "250px",
            top: "50%",
            transform: "translateY(-50%)",
            width: "20px",
            height: "40px",
            borderRadius: "0 8px 8px 0",
            cursor: "pointer",
            transition: "left 0.3s ease",
            zIndex: 1050,
          }}
          onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        >
          <i
            className={`bi ${
              isSidebarCollapsed ? "bi-chevron-right" : "bi-chevron-left"
            }`}
            style={{ fontSize: "18px" }}
          />
        </div>
      )}

      {/* Overlay for mobile when sidebar is open */}
      {!isSidebarCollapsed && isMobile && (
        <div
          className="position-fixed w-100 h-100 bg-dark bg-opacity-50"
          style={{ top: 0, left: 0, zIndex: 1040 }}
          onClick={() => setIsSidebarCollapsed(true)}
        />
      )}

      <Container fluid>
        <Row className="g-0">
          {/* LEFT SIDEBAR */}
          

          {/* MAIN CONTENT */}
          <Col md={isMobile ? 12 : 12}>
            <Container className="pt-1 bg-transparent pb-2 mb-0 ms-2 rounded">
  <h6 className="text-primary mt-1 fw-normal">APPOINTMENT NOTES</h6>
              {renderAppointmentNotes()}
            </Container>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
